import classNames from 'classnames';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OsType } from 'shared/enums/os-type.enum';
import AndroidIcon from 'src/assets/images/icons/default/AndroidIcon';
import AppleIcon from 'src/assets/images/icons/default/AppleIcon';
import {
  GAMES_CARD_CHROME_DESKTOP_REST_CLASSES,
  PlatformFilterOsType,
  PlatformFilterProps,
} from 'src/components/PlatformFilter/platform-filter-props.interface';

const PLATFORM_OPTIONS = [
  { value: OsType.Ios, label: 'Apple', Icon: AppleIcon, iconClassName: 'w-4 h-4' },
  { value: OsType.Android, label: 'Android', Icon: AndroidIcon, iconClassName: 'w-4 h-4' },
] as const;

const GAMES_CARD_CHROME_DESKTOP_HOVER_BORDER = 'md:border-[#5b98d1]';
const GAMES_CARD_CHROME_DESKTOP_HOVER_SHADOW = 'md:shadow-[2px_2px_0px_0px_#5b98d1]';
const GAMES_CARD_CHROME_DESKTOP_PRESSED_BORDER = 'md:border-[#3f54a4]';

const PlatformFilter: React.FC<PlatformFilterProps> = ({ value, onChange, size = 'big', className, id }) => {
  const isSmallSize = size === 'small';
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [pressedOption, setPressedOption] = useState<PlatformFilterOsType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(
    () => PLATFORM_OPTIONS.find((option) => option.value === value) ?? PLATFORM_OPTIONS[0],
    [value]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionSelect = useCallback(
    (osTypeId: PlatformFilterOsType) => {
      onChange(osTypeId);
      setIsOpen(false);
      setPressedOption(null);
    },
    [onChange]
  );

  const SelectedIcon = selectedOption.Icon;

  return (
    <div
      ref={containerRef}
      id={id}
      className={classNames(
        'relative inline-flex',
        isSmallSize && 'max-md:flex max-md:w-[42px] max-md:flex-col max-md:items-start max-md:gap-2.5',
        size === 'big' && 'md:self-stretch'
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsPressed(false);
        }}
        onPointerDown={() => setIsPressed(true)}
        onPointerUp={() => setIsPressed(false)}
        onPointerCancel={() => setIsPressed(false)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={classNames(
          'inline-flex h-10 min-w-[34px] min-h-[28px] items-center justify-center gap-0.5 px-[5px] py-0.5',
          'transition-[border-color,background-color,box-shadow] duration-200',
          isSmallSize
            ? classNames(
                'max-md:h-7 max-md:min-h-7 max-md:w-full max-md:gap-0.5 max-md:self-stretch max-md:rounded-lg max-md:border-0 max-md:px-0 max-md:py-0 max-md:pl-1.5 max-md:pr-0.5 max-md:shadow-none',
                isPressed ? 'max-md:bg-neutral-bold-pressed' : 'max-md:bg-neutral-bold-enabled'
              )
            : isPressed
              ? 'max-md:rounded-lg max-md:border-0 max-md:bg-neutral-bold-enabled max-md:shadow-none'
              : 'max-md:rounded-xl max-md:border max-md:border-neutral-bolder-enabled max-md:bg-neutral-default-enabled',
          size === 'big'
            ? 'md:h-auto md:min-w-[34px] md:min-h-[28px] md:gap-1 md:px-[5px] md:py-1'
            : 'md:h-[34px] md:min-h-[28px] md:gap-1 md:py-0.5 md:pl-1 md:pr-1.5',
          'md:rounded-xl md:border md:bg-neutral-default-enabled',
          isPressed
            ? classNames(GAMES_CARD_CHROME_DESKTOP_PRESSED_BORDER, 'md:shadow-none')
            : isHovered
              ? classNames(GAMES_CARD_CHROME_DESKTOP_HOVER_BORDER, GAMES_CARD_CHROME_DESKTOP_HOVER_SHADOW)
              : GAMES_CARD_CHROME_DESKTOP_REST_CLASSES,
          className
        )}
      >
        {isSmallSize ? (
          <>
            <SelectedIcon className="h-4 w-4 md:hidden" />
            <span className="hidden md:flex md:h-[26px] md:w-[26px] md:aspect-square items-center justify-center rounded-lg bg-neutral-bold-enabled pb-px">
              <SelectedIcon className={selectedOption.iconClassName} />
            </span>
          </>
        ) : (
          <span
            className={classNames(
              'flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-neutral-bold-enabled pb-px',
              size === 'big' ? 'md:h-8 md:w-8' : 'md:h-[26px] md:w-[26px] md:aspect-square'
            )}
          >
            <SelectedIcon className={selectedOption.iconClassName} />
          </span>
        )}
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute -bottom-24 -left-[66px] z-50 flex w-[132px] flex-col items-start rounded-2xl border border-neutral-default-enabled bg-[rgba(255,255,255,0.95)] p-1 shadow-[0_0_8px_0_rgba(62,63,95,0.10)] backdrop-blur-[3px]"
        >
          {PLATFORM_OPTIONS.map((option) => {
            const isSelected = option.value === value;
            const isOptionPressed = pressedOption === option.value;
            const OptionIcon = option.Icon;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onPointerDown={() => setPressedOption(option.value)}
                onPointerUp={() => setPressedOption(null)}
                onPointerLeave={() => setPressedOption((current) => (current === option.value ? null : current))}
                onClick={() => handleOptionSelect(option.value)}
                className={classNames(
                  'flex h-10 w-[124px] items-center gap-2 rounded-xl p-2.5 transition-colors',
                  isOptionPressed ? 'bg-neutral-subtle-onLight-pressed' : 'bg-neutral-default-enabled'
                )}
              >
                <span className="flex h-4 flex-1 items-center gap-1">
                  <OptionIcon className={option.iconClassName} />
                  <span className="flex-1 text-left font-manrope text-[14px] font-normal leading-none tracking-[-0.28px] text-slate-800">
                    {option.label}
                  </span>
                </span>
                {isSelected && <CheckCircle2 className="h-4 w-4 text-[#3f54a4]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlatformFilter;
